import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Loader2, MapPin, BadgeCheck, Building2, Calendar } from 'lucide-react';
import api from '../../lib/api';
import { LOGO_DARK_BG } from '../../lib/constants';

export default function PublicCard() {
  const { token } = useParams();
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api
      .get(`/p/${token}`)
      .then(({ data }) => setMember(data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cnib-navy">
        <Loader2 className="h-10 w-10 animate-spin text-orange-500" />
      </div>
    );
  }

  if (notFound || !member) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-cnib-navy px-4 text-center">
        <h1 className="font-heading text-2xl font-bold text-white">Credencial no encontrada o revocada</h1>
        <Link to="/" className="rounded-full bg-orange-500 px-6 py-3 font-bold text-white">
          Ir al inicio
        </Link>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-cnib-navy px-4 py-24" data-testid="public-member-card">
      <div className="w-full max-w-md overflow-hidden rounded-3xl border border-white/10 bg-white/5 shadow-2xl backdrop-blur-md">
        <div className="flex items-center justify-between bg-cnib-navydeep px-6 py-4">
          <img src={LOGO_DARK_BG} alt="CNIB" className="h-8 w-auto" />
          <span className="rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-bold text-emerald-400">
            {member.status === 'active' ? 'Socio vigente' : 'No vigente'}
          </span>
        </div>
        <div className="p-8 text-center">
          <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-cnib-blue/20 font-heading text-2xl font-extrabold text-orange-500">
            {member.picture ? (
              <img src={member.picture} alt={member.name} className="h-full w-full rounded-full object-cover" />
            ) : (
              member.name?.charAt(0)
            )}
          </div>
          <h1 className="mt-4 font-heading text-2xl font-bold text-white">{member.name}</h1>
          <p className="mt-1 flex items-center justify-center gap-1.5 text-sm font-semibold text-orange-400">
            <BadgeCheck className="h-4 w-4" /> {member.category || 'Socio CNIB'}
          </p>
          <div className="mt-6 space-y-3 rounded-2xl bg-cnib-navydeep/60 p-5 text-left text-sm">
            <p className="flex items-center justify-between text-slate-300">
              <span className="text-slate-500">N° de socio</span>
              <span className="font-mono font-bold text-white">{member.member_number}</span>
            </p>
            {member.company && (
              <p className="flex items-center justify-between text-slate-300">
                <span className="flex items-center gap-1.5 text-slate-500"><Building2 className="h-3.5 w-3.5" /> Empresa</span>
                <span className="font-semibold text-white">{member.company}</span>
              </p>
            )}
            {member.city && (
              <p className="flex items-center justify-between text-slate-300">
                <span className="flex items-center gap-1.5 text-slate-500"><MapPin className="h-3.5 w-3.5" /> Ciudad</span>
                <span className="font-semibold text-white">{member.city}</span>
              </p>
            )}
            {member.valid_until && (
              <p className="flex items-center justify-between text-slate-300">
                <span className="flex items-center gap-1.5 text-slate-500"><Calendar className="h-3.5 w-3.5" /> Vigente hasta</span>
                <span className="font-semibold text-white">
                  {new Date(member.valid_until).toLocaleDateString('es-BO', { day: 'numeric', month: 'long', year: 'numeric' })}
                </span>
              </p>
            )}
          </div>
          <p className="mt-6 text-xs text-slate-500">
            Tarjeta profesional verificada por la Cámara Nacional Inmobiliaria de Bolivia.
          </p>
        </div>
      </div>
    </div>
  );
}
